import { CheckIcon, ChevronDownIcon, ChevronRightIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import type { FunctionComponent, ReactNode } from "react";
import { Fragment } from "react";
import { generatePath, NavLink } from "react-router-dom";

import TableHeader from "~/components/Table/components/TableHeader";
import TableHeaderCell from "~/components/Table/components/TableHeaderCell";
import TableWrapper from "~/components/Table/components/TableWrapper";
import { PROJECT_DETAILS_PATH_WITH_DEFAULT_TAB_ID, TIME_TRACKINGS_PATH } from "~/constants/appRoutes.ts";
import { CareerLevelId } from "~/modules/humanResources/api/careerLevel/careerLevelTypes.ts";
import type {
	TeamUtilisationMonth,
	TeamUtilisationRow,
} from "~/modules/reports/api/utilisationReport/utilisationReportTypes.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

export type ResolvedProjectRow = TeamUtilisationRow & {
	label: string;
};

export type ResolvedMember = {
	staffMemberId: string;
	currentFtePercentage: string | null;
	userId: string;
	name: string;
	mainRow?: TeamUtilisationRow;
	projectRows: ResolvedProjectRow[];
	internalRow?: TeamUtilisationRow;
	salesRow?: TeamUtilisationRow;
};

export type ResolvedGroup = {
	careerLevelId: string;
	displayName: string;
	members: ResolvedMember[];
};

type Props = {
	groups: ResolvedGroup[];
	months: TeamUtilisationMonth[];
	collapsedGroups: Set<string>;
	collapsedMembers: Set<string>;
	selectedUserIds: Set<string>;
	showDaysTracked: boolean;
	showSalesAndInternal: boolean;
	showSelection: boolean;
	onToggleGroup: (careerLevelId: string) => void;
	onToggleMember: (staffMemberId: string) => void;
	onAddUser: (userId: string) => void;
	onRemoveUser: (userId: string) => void;
};

const cellFor = (row: TeamUtilisationRow | undefined, month: TeamUtilisationMonth) =>
	row?.cells.find((cell) => cell.year === month.year && cell.month === month.month);

// The table shows one decimal. The per-user total (main minus internal and sales) also
// works from these rounded numbers, so the shown total matches the shown rows.
const roundToOneDecimal = (value: number) => Math.round(value * 10) / 10;

const timeTrackingPath = (userId: string, month: TeamUtilisationMonth) =>
	generatePath(TIME_TRACKINGS_PATH, { userId }) + `?year=${month.year}&month=${month.month}`;

// The current FTE percentage as person days of a 5-day work week: 80 % -> "4 PT".
const personDayDisplayParts = (currentFtePercentage: string | null): {
	fteText: string;
	personDaysText: string
} | undefined => {
	if (currentFtePercentage === null) return undefined;

	const percentage = Number(currentFtePercentage);
	if (Number.isNaN(percentage)) return undefined;
	const fteRatio = percentage / 10000;
	const personDays = (fteRatio * 5);
	const personDaysRounded = Math.round(personDays * 10) / 10;

	let personDaysText = formatNumberWithComma(personDaysRounded, 1, true);
	if (personDays !== personDaysRounded) {
		personDaysText = "~" + personDaysText;
	}

	const fteRounded = Math.round(fteRatio * 10) / 10;
	let fteText = formatNumberWithComma(fteRounded, 1, true);

	if (fteRatio !== fteRounded) {
		fteText = "~" + fteText;
	}
	return { fteText, personDaysText };
};

const renderValues = (averageDaysPerWeek: number | undefined,
	daysTracked: number | undefined,
	showDaysTracked: boolean): ReactNode => {
	const averageText = averageDaysPerWeek ? formatNumberWithComma(averageDaysPerWeek, 1) : "--";
	const trackedText = daysTracked ? formatNumberWithComma(daysTracked, 1) : "--";

	if (averageText === "--" && (!showDaysTracked || trackedText === "--")) {
		return <span className="text-gray-400">--</span>;
	}

	return <div className="flex flex-col text-sm">
		<span>{averageText}</span>
		{showDaysTracked && <span className="text-xs text-gray-500">{trackedText}</span>}
	</div>;
};

const TeamUtilisationTable: FunctionComponent<Props> = ({
	groups,
	months,
	collapsedGroups,
	collapsedMembers,
	selectedUserIds,
	showDaysTracked,
	showSalesAndInternal,
	showSelection,
	onToggleGroup,
	onToggleMember,
	onAddUser,
	onRemoveUser,
}) => {
	const labelClass = "z-10 border-b border-gray-100 px-3 py-2 text-left pl-6 text-sm";
	const memberLabelClass = "z-10 border-b border-gray-100 px-3 py-2 text-left font-semibold";
	// Name column + one column per month + the selection column.
	const columnCount = months.length + 2;

	const dataCell = (userId: string, month: TeamUtilisationMonth, content: ReactNode, key: string) =>
		<td key={key}
			className="border-b border-gray-100 p-0 text-right tabular-nums">
			<NavLink to={timeTrackingPath(userId, month)}
					 className="block px-3 py-2 hover:underline">
				{content}
			</NavLink>
		</td>;

	const selectionCell = (userId: string) => {
		if (showSelection) {
			return <td className="border-b border-gray-100 px-3 py-2 text-center">
				<button type="button"
						onClick={() => onRemoveUser(userId)}
						aria-label="Aus Auswahl entfernen"
						className="text-gray-400 hover:text-red-600">
					<TrashIcon className="size-5" />
				</button>
			</td>;
		}

		if (selectedUserIds.has(userId)) {
			return <td className="border-b border-gray-100 px-3 py-2 text-center">
				<CheckIcon className="mx-auto size-5 text-green-600" />
			</td>;
		}

		return <td className="border-b border-gray-100 px-3 py-2 text-center">
			<button type="button"
					onClick={() => onAddUser(userId)}
					aria-label="Zur Auswahl hinzufügen"
					className="flex w-full justify-center text-gray-400 hover:text-accent-600">
				<PlusIcon className="size-4" />
			</button>
		</td>;
	};

	const emptySelectionCell = <td className="border-b border-gray-100" />;

	const mainValues = (member: ResolvedMember, month: TeamUtilisationMonth) => {
		const mainCell = cellFor(member.mainRow, month);

		if (showSalesAndInternal) {
			return renderValues(mainCell?.averageDaysPerWeek, mainCell?.daysTracked, showDaysTracked);
		}

		const internalCell = cellFor(member.internalRow, month);
		const salesCell = cellFor(member.salesRow, month);
		// main already contains internal and sales, so the difference is never truly
		// negative. Clamp to 0 to drop rounding artifacts like -0,0 or -0,1.
		const average = Math.max(0, roundToOneDecimal(mainCell?.averageDaysPerWeek ?? 0) - roundToOneDecimal(internalCell?.averageDaysPerWeek ?? 0) - roundToOneDecimal(salesCell?.averageDaysPerWeek ?? 0));
		const tracked = Math.max(0, roundToOneDecimal(mainCell?.daysTracked ?? 0) - roundToOneDecimal(internalCell?.daysTracked ?? 0) - roundToOneDecimal(salesCell?.daysTracked ?? 0));
		return renderValues(average, tracked, showDaysTracked);
	};

	return <div className="size-full overflow-auto">
		<TableWrapper rounded={true}>
			<TableHeader>
				<tr>
					<TableHeaderCell>
						Name
					</TableHeaderCell>
					{months.map((month) => <TableHeaderCell key={`${month.year}-${month.month}`}>
						{month.month}
					</TableHeaderCell>)}
					<TableHeaderCell>
						Auswahl
					</TableHeaderCell>
				</tr>
				<tr className="h-[0.5px]">
					<td colSpan={columnCount}
						className="bg-gray-200" />
				</tr>
			</TableHeader>
			<tbody>
			{groups.map((group) => {
				const isGroupCollapsed = collapsedGroups.has(group.careerLevelId);

				return <Fragment key={group.careerLevelId}>
					<tr>
						<td colSpan={columnCount}
							className="left-0 border-b border-gray-200 bg-primary-500 p-0 text-sm text-white">
							<button type="button"
									onClick={() => onToggleGroup(group.careerLevelId)}
									className="flex w-full items-center gap-x-1 px-3 py-1 text-left">
								{isGroupCollapsed ? <ChevronRightIcon className="size-4" /> :
									<ChevronDownIcon className="size-4" />}
								{group.displayName}
							</button>
						</td>
					</tr>
					{!isGroupCollapsed && group.members.map((member) => {
						const isMemberCollapsed = collapsedMembers.has(member.staffMemberId);
						const ptLabel = group.careerLevelId === CareerLevelId.Student ? "" : personDayDisplayParts(member.currentFtePercentage);

						return <Fragment key={member.staffMemberId}>
							<tr className="hover:bg-gray-50">
								<td className={memberLabelClass}>
									<button type="button"
											onClick={() => onToggleMember(member.staffMemberId)}
											className="flex w-full items-center gap-x-1 text-left">
										{isMemberCollapsed ? <ChevronRightIcon className="size-4" /> :
											<ChevronDownIcon className="size-4" />}
										<div className="flex w-full items-center justify-between">
											{member.name}
											{ptLabel &&
												<div className="text-xs font-normal text-gray-400">
													FTE {ptLabel.fteText} / <span className="font-bold">{ptLabel.personDaysText} PT</span>
												</div>}

										</div>
									</button>
								</td>
								{months.map((month) => dataCell(member.userId, month, mainValues(member, month), `${member.staffMemberId}-main-${month.year}-${month.month}`))}
								{selectionCell(member.userId)}
							</tr>
							{!isMemberCollapsed && member.projectRows.map((row, rowIndex) =>
								<tr key={`${member.staffMemberId}-project-${row.projectId ?? rowIndex}`}
									className="hover:bg-gray-50">
									<td className={labelClass}>
										{row.projectId
											?
											<NavLink to={generatePath(PROJECT_DETAILS_PATH_WITH_DEFAULT_TAB_ID, { projectId: row.projectId })}
													 className="hover:underline">
												{row.label}
											</NavLink>
											: row.label}
									</td>
									{months.map((month) => dataCell(member.userId, month, renderValues(cellFor(row, month)?.averageDaysPerWeek, cellFor(row, month)?.daysTracked, showDaysTracked), `${member.staffMemberId}-project-${row.projectId ?? rowIndex}-${month.year}-${month.month}`))}
									{emptySelectionCell}
								</tr>)}
							{!isMemberCollapsed && showSalesAndInternal && <tr>
								<td className={labelClass}>Intern</td>
								{months.map((month) => dataCell(member.userId, month, renderValues(cellFor(member.internalRow, month)?.averageDaysPerWeek, cellFor(member.internalRow, month)?.daysTracked, showDaysTracked), `${member.staffMemberId}-internal-${month.year}-${month.month}`))}
								{emptySelectionCell}
							</tr>}
							{!isMemberCollapsed && showSalesAndInternal && <tr>
								<td className={labelClass}>Sales</td>
								{months.map((month) => dataCell(member.userId, month, renderValues(cellFor(member.salesRow, month)?.averageDaysPerWeek, cellFor(member.salesRow, month)?.daysTracked, showDaysTracked), `${member.staffMemberId}-sales-${month.year}-${month.month}`))}
								{emptySelectionCell}
							</tr>}
						</Fragment>;
					})}
				</Fragment>;
			})}
			</tbody>
		</TableWrapper>
	</div>;
};

export default TeamUtilisationTable;
